<script lang="ts" setup>
import { BaseValidation } from "@vuelidate/core";
import DOMPurify from "dompurify";
import { defineEmits, ref, computed, defineExpose } from "vue";

type Props = {
  name?: string
  modelValue?: string | number
  type?: string
  placeholder?: string
  defaultBg?: boolean
  customWidth?: string
  customMaxWidth?: string
  disabled?: boolean
  supportText?: string
  validation?: BaseValidation
  customHeight?: string
  customHighlight?: boolean
  shadow?: boolean
  disableAttrs?: boolean
  min?: string | number
  max?: string | number
  step?: string | number
};

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  customWidth: "200px",
  customHeight: "40",
  shadow: true,
  disableAttrs: false,
});
/**
 * Variable for input type - password
 */
const isShowPassword = ref(false);
/**
 * computed for change input type if we have default type - password
 * if we have default type - any without password we return default type
 */
const currentInputType = computed(() => {
  if (props.type === "password") {
    return isShowPassword.value ? "text" : "password";
  }
  return props.type;
});

const inputRef = ref<HTMLInputElement | null>(null);

/**
 * Function for change input type if we have default type - password
 */
const changeInputType = () => {
  isShowPassword.value = !isShowPassword.value;
};

const hideInitialPassword = () => {
  isShowPassword.value = false;
};

const emit = defineEmits<{
  (event: "update:modelValue", value: string): void
  (event: "customOnChange", data: Event): void
  (event: "clearExternalError", field: string): void
  (event: "resetHiddenPassword"): void
}>();

function handleInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;
  if (props.validation?.$externalResults) {
    emit("clearExternalError", props.validation.$path);
  }
  const sanitizedValue = DOMPurify.sanitize(value).trim();

  emit("update:modelValue", sanitizedValue);
}

const calculateIconRightPosition = () => {
  const currentHeight = +props.customHeight;

  if (currentHeight > 50 && !props.validation?.$error) {
    return "33%";
  }

  if (currentHeight > 50 && props.validation?.$error) {
    return "23%";
  }

  if (currentHeight < 50) {
    return "25%";
  }
};

const calculateIconLeftPosition = () => {
  const currentHeight = +props.customHeight;

  if (currentHeight > 50) {
    return "-1%";
  }

  if (currentHeight < 50) {
    return "-6%";
  }
};

defineExpose({ inputRef, hideInitialPassword });
</script>

<template>
  <div>
    <label class="label font-normal text-[14px] text-secondaryText">{{
      name
    }}</label>
    <div class="w-full flex flex-col">
      <div class="relative">
        <span
          :class="{
            'w-0': !$slots['icon-left'],
            'w-[25px]': $slots['icon-left'],
          }"
          :style="{ top: calculateIconLeftPosition() }"
          class="absolute left-[5%] h-full flex justify-center items-center"
        >
          <slot name="icon-left" />
        </span>
        <input
          ref="inputRef"
          :class="{
            'input-style': props.type !== 'file',
            'input-file': props.type === 'file',
            'left-slot': $slots['icon-left'],
            'right-slot': $slots['icon-right'] || props.type === 'password',
            errorBorder: validation?.$error || props.customHighlight,
            'cursor-pointer': type === 'date',
            'shadow-md dark:shadow-slate-900/50': props.shadow,
            'bg-mainBg cursor-not-allowed': props.disabled,
            'bg-inputBg': !props.disabled,
          }"
          :disabled="disabled"
          :max="props.type === 'number' ? props?.max : undefined"
          :min="props.type === 'number' ? props?.min : undefined"
          :placeholder="placeholder"
          :step="props.type === 'number' ? props?.step : undefined"
          :style="{
            'min-width': props.customWidth,
            height: props.customHeight,
            'max-width': props.customMaxWidth || '100%',
          }"
          :type="currentInputType"
          :value="modelValue"
          class="general"
          v-bind="disableAttrs ? {} : $attrs"
          @change="(event) => emit('customOnChange', event)"
          @input="handleInput"
          @wheel.prevent
        >
        <span
          :style="{ top: calculateIconRightPosition() }"
          class="absolute right-[5%] w-[25px] flex justify-center items-center"
        >
          <slot name="icon-right" />
          <div
            v-if="props.type === 'password'"
            class="cursor-pointer"
          >
            <span
              v-if="currentInputType === 'text'"
              class="w-[20px] stroke-svgColor"
              @click="changeInputType"
            >
              <feather-icon
                class="ml-1 cursor-pointer"
                size="16"
                type="eye-off"
              />
            </span>
            <span
              v-else
              class="w-[20px] stroke-svgColor"
              @click="changeInputType"
            >
              <feather-icon
                class="ml-1 cursor-pointer"
                size="16"
                type="eye"
              />
            </span>
          </div>
        </span>
      </div>

      <span
        v-if="currentInputType === 'file' && !$slots['icon-right']"
        class="absolute right-[5%] top-[25%] flex justify-center items-center"
      >
        <span class="w-[20px] cursor-pointer"> PaperClipIcon </span>
      </span>

      <p
        v-if="supportText"
        class="text-[12px] text-secondaryText"
      >
        {{ supportText }}
      </p>

      <span
        :class="{ showMessage: validation?.$error }"
        class="errorMessage"
      >
        <p
          v-if="validation?.$error"
          class="font-regular"
        >
          {{ validation?.$errors[0]?.$message }}
        </p>
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
input[type="file"]::file-selector-button {
  display: none;
}

.general {
  @apply transition-all duration-500  text-mainText hover:shadow-lg
  outline-none focus:shadow-lg pl-[50px];
}

.input-style:focus {
  @apply border-primary;
}

.input-style {
  @apply h-[40px] rounded-md px-4 w-full flex items-center font-light;
}

.left-slot {
  padding-left: 50px;
}

.right-slot {
  padding-right: 50px;
}

.input-file {
  @apply border-2 border-primary text-[0.8rem] p-[10px]
  cursor-pointer rounded-lg  flex items-center;
}

.errorBorder {
  transition: all 0.1s;
  @apply border-2 border-red-500;
}

.errorMessage {
  @apply opacity-0 z-[1] invisible text-red-500 text-[0.8rem] pt-1;
  transform: translateY(0px);
  transition: transform 0.8s ease,
  opacity 0.4s ease;
}

.showMessage {
  @apply opacity-100 visible;
  transform: translateY(5px);
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

/* Hide spin buttons in Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
